import React from 'react';
import { CheckIcon, EyeIcon, LayoutGridIcon, MessageSquareTextIcon, SlidersHorizontalIcon, TargetIcon, TimerIcon } from 'lucide-react';
import { SectionHeading } from '../ui/SectionHeading';
import { CtaButton } from '../ui/CtaButton';
import { COVER_IMAGE, coreGuideFacts } from '../../data/site';
import { guideFeatures, GuideFeatureIcon, teachingSteps } from '../../data/guideFeatures';
const icons: Record<GuideFeatureIcon, typeof TargetIcon> = {
  target: TargetIcon,
  message: MessageSquareTextIcon,
  layout: LayoutGridIcon,
  timer: TimerIcon,
  sliders: SlidersHorizontalIcon,
  eye: EyeIcon
};
export function CoreContent() {
  return <section aria-labelledby="content-title" className="border-t border-line bg-mint py-20 text-ink lg:py-24" data-id="element-438" data-mp="134ol8blfm4qe">
      <div className="mx-auto max-w-page px-5 lg:px-8" data-id="element-439" data-mp="183hyc2wt3tq1">
        <SectionHeading id="content-title" tone="light" eyebrow="The core guide" lead="Everything you need" highlight="to teach the unit" data-id="element-440" data-mp="u7e9kp4pcdky" />

        <article className="mt-12 grid items-center gap-8 rounded-xl border border-line bg-white p-6 sm:p-8 md:grid-cols-[220px_1fr] md:gap-10 lg:p-10" data-id="element-441" data-mp="1fowm60his7cf">
          <div className="mx-auto w-40 md:w-full" data-id="element-442" data-mp="1podtsngc0vb1">
            <img src={COVER_IMAGE} alt="Actual PDF cover: 12 Flag Football Lessons. Fully Planned." loading="lazy" className="block h-auto w-full shadow-[0_18px_36px_-16px_rgba(0,0,0,0.45)]" data-id="element-443" data-mp="1pnvl2rchgom8" />
          </div>
          <div data-id="element-444" data-mp="s1et81mir37v">
            <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-445" data-mp="s1y6v654z5g3">Core guide · In production</p>
            <h3 className="mt-2 font-display text-[30px] font-bold uppercase leading-[1.02] sm:text-[38px]" data-id="element-446" data-mp="16quhnaykuwke">
              Your school year of flag football, ready to teach
            </h3>
            <p className="mt-3 text-base leading-relaxed text-body sm:text-lg" data-id="element-447" data-mp="e7nnz5ftm5hy">
              One practical program that takes your class from flags and safety to small-sided games across the year, with a complete 40-minute plan and a five-minute extension for every class.
            </p>
            <ul className="mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-448" data-mp="2e9x2959sgu6p">
              {coreGuideFacts.map(fact => <li key={fact} className="flex gap-2.5 text-base" data-id="element-449" data-mp="2j4b15hnntjg4">
                  <CheckIcon aria-hidden="true" strokeWidth={3} className="mt-1 h-4 w-4 shrink-0 text-gold-dark" data-id="element-450" data-mp="2n2i6zhgl50cj" />
                  {fact}
                </li>)}
            </ul>
          </div>
        </article>

        <h3 className="mt-16 text-center font-display text-[30px] font-bold uppercase leading-none sm:text-[36px]" data-id="element-451" data-mp="1vcvuxdsrihzl">
          In every class
        </h3>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5" data-id="element-452" data-mp="24dk9c9m2ikd0">
          {guideFeatures.map(feature => {
          const Icon = icons[feature.icon];
          return <li key={feature.label} className="flex gap-4 rounded-xl border border-line bg-white p-6" data-id="element-453" data-mp="3frxqkvmbg1k2">
                <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center" style={{
              backgroundColor: feature.bg,
              color: feature.fg
            }} data-id="element-454" data-mp="3pwnomqelmlj0">
                  <Icon className="h-6 w-6" strokeWidth={2.25} data-id="element-455" data-mp="3g4rqjfciives" />
                </span>
                <div data-id="element-456" data-mp="3c4hjdzczhyyc">
                  <h4 className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-457" data-mp="3c2wog8mrcdzy">{feature.label}</h4>
                  <p className="mt-1.5 text-base leading-relaxed text-body" data-id="element-458" data-mp="326te4fhr591x">{feature.text}</p>
                </div>
              </li>;
        })}
        </ul>

        <div className="mt-16 rounded-xl bg-cream px-6 py-10 sm:px-10" data-id="element-459" data-mp="2x8097bgj6qdd">
          <h3 className="text-center font-display text-[30px] font-bold uppercase leading-none sm:text-[36px]" data-id="element-460" data-mp="2t7gcua3s7qsa">
            How it works
          </h3>
          <ol className="mt-8 grid gap-8 md:grid-cols-3 md:gap-8" data-id="element-461" data-mp="res4xng70f66">
            {teachingSteps.map((step, index) => <li key={step.title} className="flex gap-4 md:flex-col md:gap-3" data-id="element-462" data-mp="yhgm115u1qzr" data-mpi={index}>
                <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center bg-forest font-display text-[26px] font-bold text-gold" data-id="element-463" data-mp="13h1gozdyzd6o">
                  {index + 1}
                </span>
                <div data-id="element-464" data-mp="th2c1dko86z3">
                  <h4 className="font-display text-[24px] font-bold uppercase leading-tight" data-id="element-465" data-mp="tkq2g3utkd9l">
                    <span className="sr-only" data-id="element-466" data-mp="fpnoqujqdjm8">Step {index + 1}: </span>
                    {step.title}
                  </h4>
                  <p className="mt-1 text-base leading-relaxed text-body" data-id="element-467" data-mp="5tt2zqk7n8f8">{step.text}</p>
                </div>
              </li>)}
          </ol>
        </div>

        <div className="mt-12 text-center" data-id="element-468" data-mp="aq1d2pjjwbfj">
          <CtaButton target="offer" className="w-full sm:w-auto" data-id="element-469" data-mp="3sxqvyrn03z6m">
            See the complete kit
          </CtaButton>
        </div>
      </div>
    </section>;
}